import { Button } from '@src/design-system/button';
import { createRoute } from '@src/core/router/paths';
import { ArrowLeft } from 'iconsax-react';
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '@shared/components/reusables/loader';
import { ErrorMessage, Form, Formik } from 'formik';
import { Textfield } from '@shared/fields/text-field';
import type { TEditLeadBody } from '@features/leads/services/lead-api';
import { Text } from '@src/design-system/text';
import { Label } from '@src/design-system/label';
import { PhoneNumberInput } from '@src/design-system/phone-number-input';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { SelectField } from '@shared/fields/select-field';
import { getLocalTimeZone, today } from '@internationalized/date';
import { LanguageLevels } from '@shared/constants/language-levels';
import { editMainInfoSchema } from '../constants';
import { FormikDatePicker } from '@shared/fields/formik-date-picker-field';
import leadApi from '@features/leads/services/lead-api';

const EditMainInfo = () => {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const { data: leadData, isLoading } = leadApi.useFetchOne({
    id: leadId || 0,
  });
  const { mutateAsync: editLead, isPending } = leadApi.useUpdate();

  const initialValues = {
    ...leadData,
    whatsapp_number: leadData?.whatsapp_number ?? '',
  } as unknown as TEditLeadBody;

  const handleEditLead = (values: TEditLeadBody) => {
    const isFieldChanged = (fieldName: keyof TEditLeadBody) => {
      return (
        JSON.stringify(values[fieldName]) !==
        JSON.stringify(initialValues[fieldName])
      );
    };

    if (!leadData) return;

    const dataToSend = {
      ...values,
      programs: leadData.programs?.map((res) => res.id),
      assignee: leadData.assignee?.id,
      status: leadData.status?.id,
      referal: leadData.referal?.id,
      labels: leadData.labels?.map((res) => res.id),
      student: leadData.student?.id,
    };

    const onlyChangedFields = Object.fromEntries(
      Object.entries(dataToSend).filter(([key]) =>
        isFieldChanged(key as keyof TEditLeadBody)
      )
    );
    editLead({ id: leadData.id, ...onlyChangedFields }).then(() =>
      navigate(createRoute(`/leads/${leadId}`))
    );
  };

  if (!leadId) return null;

  return (
    <div className='relative bg-primary-30 h-full'>
      <div className='flex justify-between bg-white items-center p-6'>
        <h1 className='text-2xl font-semibold text-gray-1000 flex items-center'>
          <Button
            variant='primaryLink'
            size={'iconSm'}
            onClick={() => navigate(createRoute(`/leads/${leadId}`))}
          >
            <ArrowLeft />
          </Button>
          <p className='text-2xl font-semibold text-gray-1000'>
            Edit Main Info
          </p>
        </h1>
      </div>
      {isLoading ? (
        <Loader />
      ) : (
        <Formik
          initialValues={
            leadData ? initialValues : ({} as unknown as TEditLeadBody)
          }
          validationSchema={editMainInfoSchema}
          onSubmit={(values) => handleEditLead(values)}
        >
          {({ dirty, setFieldValue, values }) => (
            <Form className='h-full'>
              <div className='p-5 min-h-11/12 bg-primary-30'>
                <div className='bg-white rounded-xl p-5 mb-5 border border-primary-100'>
                  <Text className='text-gray-600 font-medium text-base mb-6'>
                    Main Information
                  </Text>
                  <Textfield
                    className='mb-3'
                    name='name'
                    type='text'
                    placeholder='Name'
                    label='Name'
                  />
                  <Textfield
                    className='mb-3'
                    name='email'
                    type='email'
                    placeholder='Email'
                    label='Email'
                  />
                  <div className='flex flex-col gap-1'>
                    <Label className='mb-2'>Phone Number</Label>
                    <PhoneNumberInput
                      name='phone_number'
                      placeholder='ex: +20124356762'
                      onChange={(value) => setFieldValue('phone_number', value)}
                      value={values['phone_number']}
                    />
                    <ErrorMessage
                      name='phone_number'
                      component={CustomErrorMessage}
                    />
                  </div>
                  <div className='flex flex-col gap-1 my-3'>
                    <Label className='mb-2'>Whatsapp Number</Label>
                    <PhoneNumberInput
                      name='whatsapp_number'
                      placeholder='ex: +20124356762'
                      onChange={(value) =>
                        setFieldValue('whatsapp_number', value)
                      }
                      value={values['whatsapp_number']}
                    />
                    <ErrorMessage
                      name='whatsapp_number'
                      component={CustomErrorMessage}
                    />
                  </div>
                  <div className='mb-3'>
                    <FormikDatePicker
                      name='birth_date'
                      maxValue={today(getLocalTimeZone())}
                      label='Date of Birth'
                    />
                  </div>
                  <div className='my-5'>
                    <Label className='mb-3'>Gender</Label>
                    <SelectField
                      options={[
                        { value: 'MALE', label: 'Male' },
                        { value: 'FEMALE', label: 'Female' },
                      ]}
                      selectName='gender'
                      selectPlaceholder='Gender'
                    />
                  </div>
                  <div className='my-5'>
                    <Label className='mb-3'>English Level</Label>
                    <SelectField
                      options={[...LanguageLevels]}
                      selectName='english_level'
                      selectPlaceholder='English Level'
                    />
                  </div>
                </div>
              </div>
              <div className='bg-white border-t-2 z-50 border-pale-border inset-x-0 bottom-0 sticky'>
                <div className='flex items-center py-4 px-5 mx-auto justify-between'>
                  <Button
                    variant='primaryOutline'
                    onClick={() => navigate(createRoute(`/leads/${leadId}`))}
                  >
                    Cancel
                  </Button>

                  <Button
                    disabled={!dirty}
                    type='submit'
                    isLoading={isPending}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

export default EditMainInfo;
